import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import Dropdown, {
  StyledContent as StyledDropdownContent,
  StyledDropdownIcon,
} from '@components/Dropdown';
import Button from '@components/Button';

import Profile from '@components/CRM/Clients/Profile';
import { PersonType } from '@components/CRM/Clients/Profile/Profile';
import Avatar from '@components/CRM/Clients/Avatar';
import Name from '@components/CRM/Clients/Name';
import SoundSwitcher from '../SoundSwitcher';
import ThemeSwitcher from '../ThemeSwitcher';
import VolumeControl from '../VolumeControl';
import { Theme } from '../ThemeSwitcher/ThemeSwitcher';

interface ProfileDropdownProps {
  className?: string;
  currentManager: PersonType;
  isAgentMode?: boolean;
  onAgentLogout?: () => void;
  volume: number;
  setVolume: (volume: number) => void;
  setIsNotificationsSoundEnabled: (isSoundEnabled: boolean) => void;
  switchTheme: (theme: Theme) => void;
}

export const ProfileDropdown = ({
  className,
  currentManager,
  isAgentMode = false,
  onAgentLogout = () => null,
  volume,
  setVolume,
  setIsNotificationsSoundEnabled,
  switchTheme,
}: LT.WithChildren<ProfileDropdownProps>): JSX.Element => {
  const headRenderer = useMemo(
    () => (
      <StyledHead>
        {isAgentMode ? (
          <StyledAgentName name={currentManager?.name} />
        ) : (
          <StyledProfile person={currentManager} />
        )}
      </StyledHead>
    ),
    [currentManager, isAgentMode]
  );

  const contentRenderer = useMemo(() => {
    if (isAgentMode) {
      return (
        <StyledAgentContent>
          <StyledLogoutButton functional onClick={onAgentLogout}>
            Выйти
          </StyledLogoutButton>
        </StyledAgentContent>
      );
    }

    const { avatarUrl, name, number } = currentManager ?? {};

    return (
      <StyledContent>
        <StyledProfileInfo>
          <Avatar url={avatarUrl} />
          <StyledContact>
            <StyledName name={name} />
            <StyledNumber>{number}</StyledNumber>
          </StyledContact>
        </StyledProfileInfo>
        <StyledSettings>
          <StyledParameter>
            <StyledParameterInfo>
              Допуск звуковых уведомлений
            </StyledParameterInfo>
            <StyledSoundSwitcher
              setIsNotificationsSoundEnabled={setIsNotificationsSoundEnabled}
            />
          </StyledParameter>
          <StyledParameter>
            <StyledParameterInfo>Темная тема</StyledParameterInfo>
            <StyledThemeSwitcher switchTheme={switchTheme} />
          </StyledParameter>
          <StyledParameter>
            <StyledParameterInfo>
              Громкость уведомлений и звонков
            </StyledParameterInfo>
            <StyledVolumeControl volume={volume} setVolume={setVolume} />
          </StyledParameter>
        </StyledSettings>
      </StyledContent>
    );
  }, [currentManager, isAgentMode, onAgentLogout]);

  return (
    <StyledProfileDropdown
      $isAgentMode={isAgentMode}
      className={className}
      headRenderer={headRenderer}
      contentRenderer={contentRenderer}
      onToggle={() => null}
    />
  );
};

const StyledAgentName = styled(Name)`
  font-size: 13px;
  font-weight: normal;
  white-space: nowrap;
`;

const StyledAgentContent = styled.div`
  width: 150px;
`;

const StyledLogoutButton = styled(Button)`
  border-radius: 4px;
  color: #4d4d4d;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const StyledVolumeControl = styled(VolumeControl)`
  width: 140px;
`;

const StyledSoundSwitcher = styled(SoundSwitcher)``;

const StyledThemeSwitcher = styled(ThemeSwitcher)``;

const StyledParameterInfo = styled.div`
  font-size: 13px;
  text-align: left;
`;

const StyledParameter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 25px;

  &:not(:last-of-type) {
    border-bottom: 1px solid #d9d9d9;
  }
`;

const StyledSettings = styled.div``;

const StyledContact = styled.div`
  display: flex;
`;

const StyledName = styled(Name)`
  font-size: 13px;
  font-weight: normal;
`;

const StyledNumber = styled.div`
  font-size: 13px;
  font-weight: normal;
  :before {
    content: '\u22C5';
    margin: 0 5px;
  }
`;

const StyledProfile = styled(Profile)`
  margin-right: 20px;
`;

const StyledProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid black;
  padding-bottom: 10px;
`;

const StyledProfileDropdown = styled(Dropdown)<{ $isAgentMode: boolean }>`
  background: white;
  font-size: 13px;
  position: relative;
  border: none;
  border-radius: 4px;
  ${StyledDropdownIcon} {
    display: none;
  }

  ${({ $isAgentMode }) =>
    $isAgentMode &&
    css`
      ${StyledDropdownContent} {
        left: auto;
        right: 0;
        transform: none;

        ::before {
          left: auto;
          margin-left: 0;
          right: 14px;
        }
      }
    `}
`;

const StyledHead = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  display: flex;
  column-gap: 5px;
  height: 45px;
  padding: 0 7px;
  overflow: hidden;
  position: relative;
  > * {
    position: relative;
    z-index: 3;
  }
`;

const StyledContent = styled.div`
  padding: 5px;
  width: 330px;
`;
